import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, DialogActions, DialogContent, DialogTitle, MenuItem } from '@mui/material';
import { AssortmentConfig, FieldTemplateDTO } from '@retail/member-config/types';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { ContainedButton, Dialog, Menu, MenuButton, OutlinedButton } from '@shared/components';
import { useCallback, useState } from 'react';
import {
  useFetchFieldTemplates,
  useUpdateAssortmentFields,
} from '@retail/member-config/data-access';
import { AssortmentsFieldsUpateTable } from '@retail/member-config/components';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  brandId: string;
  assortmentsForEdit: AssortmentConfig[];
  onClose: () => void;
}

export function AssortmentsFieldsUpdateDialog({ brandId, assortmentsForEdit, onClose }: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const t = useMyAssortmentTFunction();

  const [updatedAssortments, setUpdatedAssortments] =
    useState<AssortmentConfig[]>(assortmentsForEdit);

  const { data: fieldTemplates } = useFetchFieldTemplates({ brandId });

  const applyTemplate = useCallback((template: FieldTemplateDTO) => {
    setUpdatedAssortments((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        totalfilToSftp: true,
        totalfilFormat: template.writeAsCsv ? 'CSV' : 'VILMA',
        totalfilVilmaVersion: template.vilmaVersion,
        totalfilCharset: template.charset as AssortmentConfig['totalfilCharset'],
        totalfilSftpDirectory: template.sftpDirectory,
        totalfilNumberOfFutureDays: template.numberOfFutureDays,
        totalfilPerSupplier: template.totalfilPerSupplier,
      }))
    );
  }, []);

  const { mutateAsync: updateAssortmentFieldsAsync } = useUpdateAssortmentFields();

  const handleUpdateFields = useCallback(async () => {
    const updatedFields = updatedAssortments.map((assortment) => {
      const currentValues = assortmentsForEdit.find(
        (currentAssortment) => currentAssortment.id === assortment.id
      )!;

      return {
        id: assortment.id,
        body: {
          title: assortment.title,
          updateTitle: currentValues.title !== assortment.title,
          sendSalesPriceUpdated: assortment.sendSalesPriceUpdated,
          updateSendSalesPriceUpdated:
            currentValues.sendSalesPriceUpdated !== assortment.sendSalesPriceUpdated,
          erpType: assortment.erpType,
          updateErpType: currentValues.erpType !== assortment.erpType,
          erpId: assortment.erpId,
          updateErpId: currentValues.erpId !== assortment.erpId,
          totalfilVilmaVersion: assortment.totalfilVilmaVersion,
          updateTotalfilVilmaVersion:
            currentValues.totalfilVilmaVersion !== assortment.totalfilVilmaVersion,
          totalfilNumberOfFutureDays: assortment.totalfilNumberOfFutureDays,
          updateTotalfilNumberOfFutureDays:
            currentValues.totalfilNumberOfFutureDays !== assortment.totalfilNumberOfFutureDays,
          totalfilSftpFrequency: assortment.totalfilSftpFrequency,
          updateTotalfilSftpFrequency:
            currentValues.totalfilSftpFrequency !== assortment.totalfilSftpFrequency,
          totalfilSftpDirectory: assortment.totalfilSftpDirectory,
          updateTotalfilSftpDirectory:
            currentValues.totalfilSftpDirectory !== assortment.totalfilSftpDirectory,
          totalfilToSftp: assortment.totalfilToSftp,
          updateTotalfilToSftp: currentValues.totalfilToSftp !== assortment.totalfilToSftp,
          totalfilCharset: assortment.totalfilCharset,
          updateTotalfilCharset: currentValues.totalfilCharset !== assortment.totalfilCharset,
          totalfilFormat: assortment.totalfilFormat,
          updateTotalfilFormat: currentValues.totalfilFormat !== assortment.totalfilFormat,
        },
      };
    });
    try {
      const mutationPromises = updatedFields.map((assortment) =>
        updateAssortmentFieldsAsync({
          urlVariables: { assortmentId: assortment.id },
          body: assortment.body,
        })
      );
      await Promise.all(mutationPromises);
    } catch (e) {
      console.error(e);
      toast.error(t('myAssortment.actions.genericActionError'));
    } finally {
      onClose();
      queryClient.invalidateQueries({ queryKey: ['member-config'] });
      toast.success(t('myAssortment.memberConfig.fieldsAreUpdated'));
    }
  }, [
    assortmentsForEdit,
    onClose,
    queryClient,
    t,
    toast,
    updateAssortmentFieldsAsync,
    updatedAssortments,
  ]);

  return (
    <Dialog open onClose={onClose} maxWidth="3xl" fullWidth>
      <DialogTitle sx={{ justifyContent: 'space-between' }}>
        {t('myAssortment.memberConfig.editAssortmentValues')}
        <Menu>
          {({ isOpen, onClose: closeMenu }) => (
            <Box>
              <MenuButton
                component={OutlinedButton}
                size="small"
                disabled={!fieldTemplates?.length}
                endIcon={
                  <KeyboardArrowDown
                    sx={{
                      transition: 'transform 0.2s',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                }
              >
                {t('myAssortment.memberConfig.useTemplate')}
              </MenuButton>
              {fieldTemplates?.map((template) => (
                <MenuItem
                  key={template.name}
                  onClick={() => {
                    applyTemplate(template);
                    closeMenu();
                  }}
                >
                  {template.name}
                </MenuItem>
              ))}
            </Box>
          )}
        </Menu>
      </DialogTitle>
      <DialogContent className="padding-0" sx={{ py: '24px !important' }}>
        <AssortmentsFieldsUpateTable
          updatedRows={updatedAssortments}
          setUpdatedRows={setUpdatedAssortments}
        />
      </DialogContent>
      <DialogActions>
        <OutlinedButton size="small" onClick={onClose}>
          {t('myAssortment.cancel')}
        </OutlinedButton>
        <ContainedButton size="small" onClick={handleUpdateFields}>
          {t('myAssortment.update')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}
